import React from "react";
import PropTypes from "prop-types";
import { Link, graphql } from "gatsby";
import { getImage } from "gatsby-plugin-image";

import Layout from "../components/Layout";
import Features from "../components/Features";
import BlogRoll from "../components/BlogRoll";
import FullWidthImage from "../components/FullWidthImage";

// eslint-disable-next-line
export const IndexPageTemplate = ({
  heroImageExport,
  icebergImageExport,
  contentImageExport,
  title,
  heading,
  subheading,
  mainpitch,
  description,
  intro,
}) => {
  const heroImage = getImage(heroImageExport) || heroImageExport;
  const iceberg = getImage(icebergImageExport) || icebergImageExport;
  const contentImage = getImage(contentImageExport) || contentImageExport;

  return (
    <div>
      <FullWidthImage img={heroImage} title={title} subheading={subheading} />
      <section className="section section--gradient">
        <div className="container">
          <div className="section">
            <div className="columns">
              <div className="column is-10 is-offset-1">
                <div className="content">
                  <div className="content">
                    <div className="tile">
                      <h1 className="title">{mainpitch.title}</h1>
                    </div>
                    <div className="tile" style={{ marginBottom: "1.5rem" }}>
                      <p className="subtitle">{mainpitch.description}</p>
                    </div>
                    <p className="subtitle" style={{ marginBottom: "2rem" }}>
                      Now imagine your next vacation. What comes to mind? Breathing in crisp mountain air on a hike? Sinking your toes into warm sand as waves roll in? Exploring a buzzing city filled with standout meals and unforgettable energy? Maybe it's a road trip, a camping adventure, a cruise, or a relaxing stay at an all-inclusive resort.
                    </p>
                    <div style={{ marginBottom: "2rem" }}>
                      <FullWidthImage img={contentImage} height={"100%"} width={"100%"} imgPosition={"contain"} />
                    </div>
                    <p className="subtitle" style={{ marginBottom: "1.25rem" }}>
                      We travel because we are fundamentally explorers. Our bodies are built to move. The desire to discover and experience the world around us is evolutionary. We are direct descendants of nomadic hunter-gatherers, for whom moving from place to place was essential for survival. That instinct to explore remains deeply rooted in our biology—even if our survival no longer depends on it.
                    </p>
                    <p className="subtitle" style={{ marginBottom: "1.25rem" }}>
                      Even in retirement—when time finally becomes abundant—travel consistently tops the list of priorities. For both traditional retirees and those pursuing <a href="https://www.reddit.com/r/Fire/" target="_blank" rel="noopener noreferrer">FIRE (Financial Independence, Retire Early)</a>, the freedom to explore is the ultimate prize. The desire to see the world doesn't fade—it only grows stronger when we finally have the time to answer it.
                    </p>
                    <p className="subtitle" style={{ marginBottom: "1.25rem" }}>
                      Why wait until retirement to start exploring? Take short getaways. Work remotely between trips. Weave travel into your everyday life. You don't need a sabbatical to see the world—just the intention to make space for it.
                    </p>
                    <p className="subtitle" style={{ marginBottom: "1.25rem" }}>
                      In the end, you won’t remember the extra hours you spent at work—you’ll remember the experiences that made you feel truly alive.
                    </p>
                    <p className="subtitle" style={{ marginBottom: "2rem" }}>
                      OptimizeMyTravel shares real-world insights and practical tips to help you travel smarter, spend wisely, and experience more.
                    </p>
                    <div style={{ marginBottom: "2rem" }}>
                      <FullWidthImage img={iceberg} height={"100%"} width={"100%"} imgPosition={"contain"} />
                    </div>
                  </div>
                  <div className="columns">
                    <div className="column is-12">
                      <h3 className="has-text-weight-semibold is-size-2">
                        {heading}
                      </h3>
                      <p>{description}</p>
                    </div>
                  </div>
                  <Features gridItems={intro.blurbs} />
                  <div className="column is-12">
                    <h3 className="has-text-weight-semibold is-size-2">
                      Latest stories
                    </h3>
                    <BlogRoll limit={8} />
                    <div className="column is-12 has-text-centered">
                      <Link className="btn" to="/blog">
                        Read more
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

IndexPageTemplate.propTypes = {
  heroImageExport: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  icebergImageExport: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  contentImageExport: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  title: PropTypes.string,
  heading: PropTypes.string,
  subheading: PropTypes.string,
  mainpitch: PropTypes.object,
  description: PropTypes.string,
  intro: PropTypes.shape({
    blurbs: PropTypes.array,
  }),
};

const IndexPage = ({ data }) => {
  const { frontmatter } = data.markdownRemark;

  return (
    <Layout>
      <IndexPageTemplate
        heroImageExport={frontmatter.heroImageExport}
        icebergImageExport={frontmatter.icebergImageExport}
        contentImageExport={frontmatter.contentImageExport}
        title={frontmatter.title}
        heading={frontmatter.heading}
        subheading={frontmatter.subheading}
        mainpitch={frontmatter.mainpitch}
        description={frontmatter.description}
        intro={frontmatter.intro}
      />
    </Layout>
  );
};

IndexPage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      frontmatter: PropTypes.object,
    }),
  }),
};

export default IndexPage;

export const pageQuery = graphql`
  query IndexPageTemplate {
    markdownRemark(frontmatter: { templateKey: { eq: "index-page" } }) {
      frontmatter {
        title
        heroImageExport {
          childImageSharp {
            gatsbyImageData(quality: 100, layout: FULL_WIDTH)
          }
        }
        contentImageExport {
          childImageSharp {
            gatsbyImageData(quality: 100, layout: CONSTRAINED)
          }
        }
        heading
        mainpitch {
          title
          description
        }
        icebergImageExport {
          childImageSharp {
            gatsbyImageData(quality: 100, layout: CONSTRAINED)
          }
        }
        description
        intro {
          blurbs {
            image {
              publicURL
              childImageSharp {
                gatsbyImageData(width: 240, quality: 64, layout: CONSTRAINED)
              }
            }
            text
          }
        }
      }
    }
  }
`;
